import {
  type Sentence,
  splitSentences,
} from "@/lib/tool-kit/text-analysis/segment";
import { STOPWORDS } from "@/lib/tool-kit/text-analysis/stopwords";

/**
 * Extractive summarisation: score every sentence, keep the best ones, print them in
 * their original order. Nothing is rewritten — every sentence in the summary is one the
 * author wrote, word for word.
 *
 * Two scoring methods, both classics:
 *
 * - **TextRank** (Mihalcea & Tarau, 2004) treats sentences as nodes in a graph, links
 *   each pair by how many content words they share, and runs PageRank over it. A
 *   sentence scores highly when it overlaps with many other sentences that also score
 *   highly — it is "central" to the text.
 * - **Word frequency** (after Luhn, 1958) counts every content word in the text and
 *   scores a sentence by the average frequency of its words, relative to the most
 *   frequent word.
 *
 * Optionally, the first sentence of each paragraph gets a boost — academic paragraphs
 * usually open with their topic sentence.
 */

export type SummaryMethod = "textrank" | "frequency";

export interface SummaryOptions {
  method: SummaryMethod;
  /** How many sentences to keep, or what percentage of the sentences. */
  length: { by: "sentences" | "percent"; value: number };
  /** Boost the first sentence of each paragraph. */
  leadBias: boolean;
}

export interface ScoredSentence extends Sentence {
  /** 0–1, relative to the best sentence. */
  score: number;
  selected: boolean;
}

export interface Keyword {
  word: string;
  count: number;
}

export interface Summary {
  sentences: ScoredSentence[];
  /** The kept sentences, in reading order. */
  selected: ScoredSentence[];
  keywords: Keyword[];
  /** Prose sentences that could be chosen (headings are not candidates). */
  candidates: number;
  /** The text had more sentences than `MAX_SUMMARY_SENTENCES`; the rest were ignored. */
  truncated: boolean;
}

/** Multiplier for each paragraph's opening sentence when `leadBias` is on. */
export const LEAD_BOOST = 1.2;
const DAMPING = 0.85;
const MAX_ITERATIONS = 100;
const TOLERANCE = 1e-6;
const MIN_TERM_LENGTH = 3;
/** Content words a sentence needs before either method will score it. */
const MIN_SENTENCE_TERMS = 3;
const KEYWORD_LIMIT = 10;

/**
 * Sentences scored at most. TextRank compares every pair, so cost grows with the square
 * of the length; a thousand sentences is a long article and still answers instantly.
 */
export const MAX_SUMMARY_SENTENCES = 1000;

const TERM_RE = /\p{L}[\p{L}\p{N}'’-]*/gu;
const POSSESSIVE_RE = /['’]s$/;
const APOSTROPHE_RE = /['’]/g;

/**
 * Fold a word to a term: lowercase, possessive and plural removed. Only plurals are
 * folded — "studies" and "study" are one term, but "studied" is another — which keeps
 * the keyword list readable and the behaviour predictable.
 */
export function termOf(word: string): string | null {
  const lower = word
    .toLowerCase()
    .replace(POSSESSIVE_RE, "")
    .replace(APOSTROPHE_RE, "");
  if (lower.length < MIN_TERM_LENGTH || STOPWORDS.has(lower)) {
    return null;
  }
  if (lower.endsWith("ies") && lower.length > 4) {
    return `${lower.slice(0, -3)}y`;
  }
  if (lower.endsWith("sses")) {
    return lower.slice(0, -2);
  }
  const keepS =
    lower.endsWith("ss") ||
    lower.endsWith("us") ||
    lower.endsWith("is") ||
    lower.length <= 3;
  return lower.endsWith("s") && !keepS ? lower.slice(0, -1) : lower;
}

interface Analysed {
  terms: string[];
  /** term → the forms it was written in, with counts (for keyword display). */
  forms: Map<string, Map<string, number>>;
}

function termsOf(text: string, forms: Analysed["forms"]): string[] {
  const terms: string[] = [];
  for (const match of text.matchAll(TERM_RE)) {
    const term = termOf(match[0]);
    if (!term) {
      continue;
    }
    terms.push(term);
    const written = match[0].toLowerCase().replace(POSSESSIVE_RE, "");
    const byForm = forms.get(term) ?? new Map<string, number>();
    byForm.set(written, (byForm.get(written) ?? 0) + 1);
    forms.set(term, byForm);
  }
  return terms;
}

/**
 * Mihalcea & Tarau's overlap: shared words over the sum of the log sentence lengths.
 *
 * Sentences with fewer than three content words are left out of the graph. The log
 * normalisation otherwise rewards them: a three-word sentence such as "Sleep also
 * prunes." shares one word with half the text and has almost nothing to divide by.
 */
function similarity(a: Set<string>, b: Set<string>): number {
  if (a.size < MIN_SENTENCE_TERMS || b.size < MIN_SENTENCE_TERMS) {
    return 0;
  }
  let shared = 0;
  for (const term of a) {
    if (b.has(term)) {
      shared++;
    }
  }
  return shared / (Math.log(a.size) + Math.log(b.size));
}

interface Edge {
  to: number;
  weight: number;
}

/** Weighted PageRank over an undirected graph given as adjacency lists. */
function pageRank(edges: Edge[][]): number[] {
  const n = edges.length;
  const outSums = edges.map((list) =>
    list.reduce((sum, edge) => sum + edge.weight, 0)
  );
  let scores = new Array<number>(n).fill(1);
  for (let iteration = 0; iteration < MAX_ITERATIONS; iteration++) {
    const next = new Array<number>(n).fill(1 - DAMPING);
    for (let j = 0; j < n; j++) {
      if (outSums[j] === 0) {
        continue;
      }
      const share = (DAMPING * scores[j]) / outSums[j];
      for (const edge of edges[j]) {
        next[edge.to] += edge.weight * share;
      }
    }
    let change = 0;
    for (let i = 0; i < n; i++) {
      change = Math.max(change, Math.abs(next[i] - scores[i]));
    }
    scores = next;
    if (change < TOLERANCE) {
      break;
    }
  }
  return scores;
}

function textRankScores(termSets: Set<string>[]): number[] {
  const edges: Edge[][] = termSets.map(() => []);
  for (let i = 0; i < termSets.length; i++) {
    for (let j = i + 1; j < termSets.length; j++) {
      const weight = similarity(termSets[i], termSets[j]);
      if (weight > 0) {
        edges[i].push({ to: j, weight });
        edges[j].push({ to: i, weight });
      }
    }
  }
  return pageRank(edges);
}

function frequencyScores(termLists: string[][]): number[] {
  const counts = new Map<string, number>();
  for (const terms of termLists) {
    for (const term of terms) {
      counts.set(term, (counts.get(term) ?? 0) + 1);
    }
  }
  const max = Math.max(1, ...counts.values());
  return termLists.map((terms) => {
    if (terms.length < MIN_SENTENCE_TERMS) {
      return 0;
    }
    const total = terms.reduce((sum, term) => sum + (counts.get(term) ?? 0), 0);
    return total / max / terms.length;
  });
}

function keywordsFrom(
  termLists: string[][],
  forms: Analysed["forms"]
): Keyword[] {
  const counts = new Map<string, number>();
  for (const terms of termLists) {
    for (const term of terms) {
      counts.set(term, (counts.get(term) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .filter(([, count]) => count >= 2)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, KEYWORD_LIMIT)
    .map(([term, count]) => {
      const written = [...(forms.get(term) ?? new Map<string, number>())];
      written.sort((a, b) => b[1] - a[1]);
      return { word: written[0]?.[0] ?? term, count };
    });
}

/** How many sentences a length setting keeps, given the number of candidates. */
export function targetCount(
  length: SummaryOptions["length"],
  candidates: number
): number {
  if (candidates === 0) {
    return 0;
  }
  const wanted =
    length.by === "sentences"
      ? Math.round(length.value)
      : Math.round((candidates * length.value) / 100);
  return Math.min(candidates, Math.max(1, wanted));
}

export function summarize(text: string, options: SummaryOptions): Summary {
  const sentences = splitSentences(text);
  const forms: Analysed["forms"] = new Map();
  const prose = sentences.filter((sentence) => !sentence.heading);
  const candidates = prose.slice(0, MAX_SUMMARY_SENTENCES);
  const termLists = candidates.map((sentence) =>
    termsOf(text.slice(sentence.start, sentence.end), forms)
  );
  const raw =
    options.method === "textrank"
      ? textRankScores(termLists.map((terms) => new Set(terms)))
      : frequencyScores(termLists);
  const boosted = raw.map((score, i) =>
    options.leadBias && candidates[i].first ? score * LEAD_BOOST : score
  );
  const keep = targetCount(options.length, candidates.length);
  const chosen = new Set(
    boosted
      .map((score, i) => ({ score, i }))
      .sort((a, b) => b.score - a.score || a.i - b.i)
      .slice(0, keep)
      .map(({ i }) => candidates[i])
  );
  const best = Math.max(0, ...boosted);
  const scoreOf = new Map(
    candidates.map((sentence, i) => [
      sentence,
      best > 0 ? boosted[i] / best : 0,
    ])
  );
  const scored = sentences.map((sentence) => ({
    ...sentence,
    score: scoreOf.get(sentence) ?? 0,
    selected: chosen.has(sentence),
  }));
  return {
    sentences: scored,
    selected: scored.filter((sentence) => sentence.selected),
    keywords: keywordsFrom(termLists, forms),
    candidates: candidates.length,
    truncated: prose.length > candidates.length,
  };
}
